import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzDrawerRef } from 'ng-zorro-antd/drawer';
import {
  IOrderRequirement,
  ICustomerUser,
  IFile,
  IDrugCard
} from '@care-portals/api-interfaces';
import {
  formTypes,
  ConsultationUtils,
  QuestionsCatalog,
  QUESTIONNAIRE_STATUSES
} from '@care-portals/data';
import { ConsultationFacade } from '../../../+store/consultation/consultation.facade';
import { CustomerFacade } from '../../../+store/customer/customer.facade';
import { FileFacade } from '../../../+store/file/file.facade';

const DRUG_CARDS_FOLDER = 'drug-cards';

interface QnrItem {
  req: IOrderRequirement;
  questions: any[];
  data: any;
}

@Component({
  selector: 'portals-emr-all-consultations-view',
  templateUrl: './all-consultations-view.component.html',
  styleUrls: ['./all-consultations-view.component.less']
})
export class AllConsultationsViewComponent implements OnInit, OnDestroy {
  @Input() requirements: IOrderRequirement[] = [];
  @Input() customer: ICustomerUser;
  @Input() orderId: number;

  qnrItems: QnrItem[] = [];
  idFiles: IFile[] = [];
  filesLoading = false;

  /** Freshest copy of the customer (store-backed); falls back to the @Input. */
  private currentCustomer: ICustomerUser;
  private isDestroyed$ = new Subject<void>();
  private requestedFileIds = new Set<string>();

  constructor(
    private readonly drawerRef: NzDrawerRef,
    private readonly consultationFacade: ConsultationFacade,
    private readonly customerFacade: CustomerFacade,
    private readonly fileFacade: FileFacade
  ) {}

  get brandName(): string {
    return (this.customer as any)?.organization || '';
  }

  ngOnInit() {
    this.qnrItems = this.requirements
      .filter(req => req.type === 'qnr' && req.qnr)
      .map(req => {
        const consultation = req.qnr;
        const formType = formTypes[consultation.type];

        let questions: any[] = [];
        if (formType) {
          const mainQuestions = ConsultationUtils.getQuestionStackFromAnswers(
            formType.questions,
            consultation.answers
          );
          const archivedQuestions = (formType.olderVersions ?? [])
            .map(version => formTypes[version]?.questions)
            .filter(Boolean)
            .map(qs => ConsultationUtils.getQuestionStackFromAnswers(qs, consultation.answers, false))
            .flat();
          const merged = Array.from(new Set([...mainQuestions, ...archivedQuestions]));
          // Filter out evaluated questions (they're shown separately) — same as CustomerConsultationComponent
          questions = merged.filter(
            q =>
              typeof q !== 'string' ||
              QuestionsCatalog.find(m => m.field === q)?.type !== 'evaluated'
          );
        }

        return { req, questions, data: { ...consultation.answers } };
      });

    this.currentCustomer = this.customer;
    this.filesLoading = true;

    // Pull the freshest copy of this customer from the store so drug-card
    // metadata reflects recent add/remove actions instead of the (possibly
    // stale) @Input snapshot the drawer was opened with.
    this.customerFacade.loadCustomer(this.customer._id);
    this.fileFacade.loadFiles(this.customer._id, '');

    combineLatest([
      this.customerFacade.customers$,
      this.fileFacade.recentFiles$
    ])
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe(([entities, files]) => {
        const fresh = entities?.[this.customer._id] as ICustomerUser | undefined;
        this.currentCustomer = fresh ?? this.customer;

        // Fetch the current cards' files individually (idempotent). We avoid
        // loading the whole drug-cards folder so orphaned files from removed
        // cards never enter the store — the filter below keys off metadata.
        (this.currentCustomer.drugCards || []).forEach(card => {
          [card.frontFileKey, card.backFileKey, card.fileKey]
            .filter((k): k is string => !!k)
            .forEach(key => {
              const storeKey = `${DRUG_CARDS_FOLDER}/${key}`;
              if (this.requestedFileIds.has(storeKey)) return;
              // Reuse a warm buffer if the file is already loaded. Re-fetching
              // would mint a fresh signed S3 URL, busting the browser cache and
              // forcing the image to re-download — the source of the lag.
              const warm = files.some(
                f => f.folder === DRUG_CARDS_FOLDER && f.name === key && f.loaded && !!f.buffer
              );
              if (warm) {
                this.requestedFileIds.add(storeKey);
                return;
              }
              this.requestedFileIds.add(storeKey);
              this.fileFacade.getFile(this.customer._id, key, DRUG_CARDS_FOLDER);
            });
        });

        this.idFiles = files.filter(f => this.isIdFile(f));
        this.filesLoading = false;
        this.idFiles.forEach(file => {
          const fileKey = file.folder ? `${file.folder}/${file.name}` : file.name;
          if (!file.loaded && !this.requestedFileIds.has(fileKey)) {
            this.requestedFileIds.add(fileKey);
            this.fileFacade.getFile(this.customer._id, file.name, file.folder || undefined);
          }
        });
      });
  }

  ngOnDestroy() {
    this.isDestroyed$.next();
    this.isDestroyed$.complete();
  }

  close() {
    this.drawerRef.close();
  }

  markAllAsReviewed() {
    const itemsToUpdate = this.qnrItems.filter(
      item => item.req.qnr.status !== 'completed'
    );

    if (itemsToUpdate.length === 0) {
      this.drawerRef.close();
      return;
    }

    let doneCount = 0;
    itemsToUpdate.forEach(item => {
      this.consultationFacade.updateConsultation(
        item.req.qnr._id,
        { status: 'completed' },
        () => {
          (item.req.qnr as any).status = 'completed';
          doneCount++;
          if (doneCount === itemsToUpdate.length) {
            this.drawerRef.close();
          }
        }
      );
    });
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'completed': return 'green';
      case 'new': return 'blue';
      case 'inprogress': return 'orange';
      case 'ready-for-np': return 'cyan';
      case 'cancelled':
      case 'rejected':
      case 'archived': return 'red';
      default: return 'default';
    }
  }

  getStatusLabel(status: string): string {
    return QUESTIONNAIRE_STATUSES[status] ?? status;
  }

  getIdFileLabel(name: string): string {
    const personaLabels: Record<string, string> = {
      'persona-id': 'Gov. photo ID (Persona)',
      'persona-selfie': 'Selfie (Persona)'
    };
    const baseName = name?.split('.')[0];
    if (personaLabels[baseName]) return personaLabels[baseName];
    if (/^drugCard-/i.test(name)) return 'Drug plan card';

    const prefix = name?.split('_')[0];
    const labels: Record<string, string> = {
      govId: 'Gov. photo ID',
      selfie: 'Selfie photo',
      drugCard1: 'Drug plan card',
      drugCard2: 'Drug plan card 2',
      drugCard3: 'Drug plan card 3'
    };
    return labels[prefix] || prefix || name;
  }

  getImageSrc(file: IFile): string {
    const raw = typeof file?.buffer === 'string' ? file.buffer.trim() : '';
    if (!raw) return '';
    if (raw.startsWith('data:') || raw.startsWith('blob:') || raw.startsWith('http')) {
      return raw;
    }
    const cleaned = raw.replace(/\s/g, '');
    const mime = file?.mimetype || 'image/jpeg';
    return `data:${mime};base64,${cleaned}`;
  }

  openImage(file: IFile) {
    const src = this.getImageSrc(file);
    if (!src) return;

    if (src.startsWith('data:')) {
      try {
        const parts = src.split(',');
        const meta = parts[0];
        const base64 = parts.slice(1).join(',');
        const mimeMatch = /data:(.*?);base64/.exec(meta);
        const mime = mimeMatch?.[1] || 'application/octet-stream';
        const binary = atob(base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }
        const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
        const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
        if (win) {
          win.addEventListener('beforeunload', () => URL.revokeObjectURL(blobUrl));
        } else {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        }
      } catch {
        window.open(src, '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open(src, '_blank', 'noopener,noreferrer');
    }
  }

  private findDrugCard(file: IFile): IDrugCard | undefined {
    const cards: IDrugCard[] = this.currentCustomer?.drugCards || [];
    return cards.find(c =>
      [c.fileKey, c.frontFileKey, c.backFileKey].includes(file.name)
    );
  }

  getDrugCardTypeLabel(file: IFile): string {
    const card = this.findDrugCard(file);
    if (!card) return '';
    return card.label || card.type || '';
  }

  getDrugCardRelationship(file: IFile): string {
    return this.findDrugCard(file)?.relationshipToCardholder || '';
  }

  private isIdFile(file: IFile): boolean {
    // Drug-card files only show if the (fresh, store-backed) metadata still
    // references them. This hides orphans left in storage by removed cards or
    // by the "Patient has no drug cards" checkbox, while a just-added card —
    // present in the refreshed customer — shows correctly.
    if (/^drugCard/i.test(file.name)) {
      return !!this.findDrugCard(file);
    }
    return file.tags?.includes('ids') || /^(govId|selfie)/i.test(file.name);
  }
}
